/**
 * SDCX 宏区（4096B）：64B 指针 + 共享数据区
 * 动作 4B：[Delay_L, Delay_H, Type, Code]；公版固定间隔 10ms → 0A 00
 * Type：0x42 键盘按下 / 0x02 键盘抬起（Type=Key, bit6=Make/Break）
 */

import {
  SDCX_MACRO_SLOT_COUNT,
  SDCX_MACRO_UI_SLOT_COUNT,
} from "./sdcxExtraKeys";
import { SDCX_UI_TO_HID_USAGE } from "./sdcxKeyEncode";

export const SDCX_MACRO_TAB_SIZE = 4096;
export const SDCX_MACRO_POINTER_BYTES = 64;
export const SDCX_MACRO_DATA_BASE = 0x40;
export const SDCX_MACRO_SLOT_PREFIX_SIZE = 0;
export const SDCX_MACRO_ACTION_SIZE = 4;
export const SDCX_MACRO_END_MARKER_SIZE = 2;

/** 公版抓包固定动作间隔（ms） */
export const SDCX_MACRO_DEFAULT_DELAY_MS = 10;

export const SDCX_MACRO_TYPE_KEY_PRESS = 0x42;
export const SDCX_MACRO_TYPE_KEY_RELEASE = 0x02;

export type SdcxMacroAction = {
  actionType: number;
  status: number;
  delay: number;
  keyCode: number;
  keyName?: string;
};

export type SdcxMacroIntervalMode = 0 | 1;

const HID_TO_UI: Readonly<Record<number, number>> = Object.fromEntries(
  Object.entries(SDCX_UI_TO_HID_USAGE).map(([ui, hid]) => [hid, Number(ui)])
);

/** 动作 Delay 等字段：Delay_L + Delay_H（小端） */
function readU16Le(data: Uint8Array, offset: number): number {
  return (data[offset] ?? 0) | ((data[offset + 1] ?? 0) << 8);
}

function writeU16Le(data: Uint8Array, offset: number, value: number): void {
  data[offset] = value & 0xff;
  data[offset + 1] = (value >> 8) & 0xff;
}

/** 宏指针区：公版抓包首包 `40 00` → M0=0x0040（Delay 同规则，小端） */
function readMacroSlotPointer(data: Uint8Array, offset: number): number {
  return readU16Le(data, offset);
}

function writeMacroSlotPointer(
  data: Uint8Array,
  offset: number,
  value: number
): void {
  writeU16Le(data, offset, value);
}

export function readSdcxMacroSlotPointer(
  tab: Uint8Array,
  macroId: number
): number {
  if (!tab?.length || macroId < 0) return 0xffff;
  return readMacroSlotPointer(tab, macroId * 2);
}

export function uiKeyCodeToSdcxMacroHid(keyCode: number): number {
  if (!Number.isFinite(keyCode) || keyCode <= 0) return 0;
  const mapped = SDCX_UI_TO_HID_USAGE[keyCode];
  if (mapped != null) return mapped & 0xff;
  if (keyCode <= 0xff) return keyCode & 0xff;
  return 0;
}

export function sdcxMacroHidToUiKeyCode(hid: number): number {
  if (!hid) return 0;
  return HID_TO_UI[hid & 0xff] ?? hid;
}

export function encodeSdcxMacroActionType(status: number): number {
  return status ? SDCX_MACRO_TYPE_KEY_PRESS : SDCX_MACRO_TYPE_KEY_RELEASE;
}

export function decodeSdcxMacroActionStatus(typeByte: number): number {
  return (typeByte & 0x40) !== 0 ? 1 : 0;
}

function isPlausibleMacroWireRecord(data: Uint8Array, offset: number): boolean {
  if (offset + SDCX_MACRO_ACTION_SIZE > data.length) return false;
  const delay = readU16Le(data, offset);
  const type = data[offset + 2] ?? 0;
  const code = data[offset + 3] ?? 0;
  if (!delay && !type && !code) return false;
  if (!delay && !type) return false;
  if (
    type !== SDCX_MACRO_TYPE_KEY_PRESS &&
    type !== SDCX_MACRO_TYPE_KEY_RELEASE
  ) {
    return false;
  }
  return code > 0;
}

/** 兼容历史误写入的槽头 0xFF 0xFF（非协议字段） */
function resolveMacroSlotActionsOffset(tab: Uint8Array, addr: number): number {
  if (addr + 2 <= tab.length && tab[addr] === 0xff && tab[addr + 1] === 0xff) {
    const next = addr + 2;
    if (isPlausibleMacroWireRecord(tab, next)) return next;
  }
  return addr;
}

export function parseSdcxMacroSlotActionsFromTab(
  tab: Uint8Array,
  macroId: number
): SdcxMacroAction[] {
  if (!tab?.length || macroId < 0 || macroId >= SDCX_MACRO_SLOT_COUNT) {
    return [];
  }
  const addr = readMacroSlotPointer(tab, macroId * 2);
  if (addr < SDCX_MACRO_DATA_BASE || addr >= tab.length) {
    return [];
  }

  const actions: SdcxMacroAction[] = [];
  let offset = resolveMacroSlotActionsOffset(tab, addr);
  const bodyEnd = tab.length;

  while (offset + SDCX_MACRO_ACTION_SIZE <= bodyEnd) {
    const delay = readU16Le(tab, offset);
    const type = tab[offset + 2] ?? 0;
    const code = tab[offset + 3] ?? 0;
    if (!delay && !type) break;
    if (!isPlausibleMacroWireRecord(tab, offset)) break;
    actions.push({
      actionType: 3,
      status: decodeSdcxMacroActionStatus(type),
      delay,
      keyCode: sdcxMacroHidToUiKeyCode(code),
    });
    offset += SDCX_MACRO_ACTION_SIZE;
  }
  return actions;
}

function normalizeActionsForDevice(
  actions: SdcxMacroAction[],
  fixedIntervalMs?: number
): SdcxMacroAction[] {
  const cleaned = (actions ?? [])
    .map((item) => ({
      actionType: Number(item.actionType) || 3,
      status: Number(item.status) ? 1 : 0,
      delay: Math.max(0, Math.round(Number(item.delay) || 0)),
      keyCode: Number(item.keyCode) || 0,
    }))
    .filter((item) => item.keyCode > 0);

  if (!cleaned.length) return cleaned;

  const interval =
    fixedIntervalMs != null
      ? Math.max(1, Math.round(fixedIntervalMs))
      : SDCX_MACRO_DEFAULT_DELAY_MS;

  return cleaned.map((item) => ({
    ...item,
    delay:
      fixedIntervalMs != null
        ? interval
        : Math.max(1, item.delay || SDCX_MACRO_DEFAULT_DELAY_MS),
  }));
}

function writeMacroSlotBody(
  tab: Uint8Array,
  offset: number,
  actions: SdcxMacroAction[]
): number {
  let cursor = offset;

  for (const action of actions) {
    const hid = uiKeyCodeToSdcxMacroHid(action.keyCode);
    if (!hid) continue;
    const delay = Math.max(0, Math.min(65535, Math.round(action.delay)));
    writeU16Le(tab, cursor, delay);
    tab[cursor + 2] = encodeSdcxMacroActionType(action.status);
    tab[cursor + 3] = hid & 0xff;
    cursor += SDCX_MACRO_ACTION_SIZE;
  }

  /** 结束标记：Delay=0 且 Type=0（协议/公版：00 00） */
  tab[cursor++] = 0x00;
  tab[cursor++] = 0x00;
  return cursor - offset;
}

function copyMacroSlotRawFromBase(
  dest: Uint8Array,
  base: Uint8Array,
  macroId: number,
  cursor: number
): number {
  const addr = readMacroSlotPointer(base, macroId * 2);
  if (addr < SDCX_MACRO_DATA_BASE || addr >= base.length) return 0;
  let end = resolveMacroSlotActionsOffset(base, addr);
  while (end + SDCX_MACRO_ACTION_SIZE <= base.length) {
    if (!isPlausibleMacroWireRecord(base, end)) break;
    end += SDCX_MACRO_ACTION_SIZE;
  }
  if (end + SDCX_MACRO_END_MARKER_SIZE <= base.length) {
    if (!base[end] && !base[end + 1]) {
      end += SDCX_MACRO_END_MARKER_SIZE;
    }
  }
  const len = end - addr;
  if (!len || cursor + len > dest.length) return 0;
  dest.set(base.subarray(addr, end), cursor);
  return len;
}

/** 写入 macro_tab：更新指定槽，其余槽尽量保留 base 内容 */
export function buildSdcxMacroTab(
  base: Uint8Array | null,
  updates: Array<{
    macroId: number;
    actions: SdcxMacroAction[];
    fixedIntervalMs?: number;
    useFixedInterval?: boolean;
  }>
): Uint8Array {
  const tab = new Uint8Array(SDCX_MACRO_TAB_SIZE);
  tab.fill(0xff, 0, SDCX_MACRO_POINTER_BYTES);
  tab.fill(0x00, SDCX_MACRO_POINTER_BYTES, SDCX_MACRO_TAB_SIZE);

  const updateMap = new Map<
    number,
    { actions: SdcxMacroAction[]; fixedIntervalMs?: number }
  >();
  for (const update of updates) {
    if (update.macroId < 0 || update.macroId >= SDCX_MACRO_SLOT_COUNT) continue;
    const actions = normalizeActionsForDevice(
      update.actions,
      update.useFixedInterval ? update.fixedIntervalMs : undefined
    );
    updateMap.set(update.macroId, {
      actions,
      fixedIntervalMs: update.fixedIntervalMs,
    });
  }

  const baseTab =
    base && base.length >= SDCX_MACRO_TAB_SIZE
      ? base.slice(0, SDCX_MACRO_TAB_SIZE)
      : null;

  let cursor = SDCX_MACRO_DATA_BASE;
  for (let macroId = 0; macroId < SDCX_MACRO_SLOT_COUNT; macroId++) {
    const update = updateMap.get(macroId);
    if (update) {
      if (!update.actions.length) {
        writeMacroSlotPointer(tab, macroId * 2, 0xffff);
        continue;
      }
      writeMacroSlotPointer(tab, macroId * 2, cursor);
      cursor += writeMacroSlotBody(tab, cursor, update.actions);
    } else if (baseTab) {
      const copied = copyMacroSlotRawFromBase(tab, baseTab, macroId, cursor);
      if (copied > 0) {
        writeMacroSlotPointer(tab, macroId * 2, cursor);
        cursor += copied;
      } else {
        writeMacroSlotPointer(tab, macroId * 2, 0xffff);
      }
    } else {
      writeMacroSlotPointer(tab, macroId * 2, 0xffff);
    }
    if (cursor >= SDCX_MACRO_TAB_SIZE) break;
  }

  return tab;
}

export function buildSdcxMacroTabSingleSlotUpdate(
  base: Uint8Array | null,
  macroId: number,
  actions: SdcxMacroAction[],
  options?: { useFixedInterval?: boolean; fixedIntervalMs?: number }
): Uint8Array {
  return buildSdcxMacroTab(base, [
    {
      macroId,
      actions,
      useFixedInterval: options?.useFixedInterval,
      fixedIntervalMs: options?.fixedIntervalMs,
    },
  ]);
}

export function parseSdcxMacroTabSlotSummaries(tab: Uint8Array): Array<{
  macroId: number;
  valid: boolean;
  actNum: number;
  actions: SdcxMacroAction[];
}> {
  return Array.from({ length: SDCX_MACRO_UI_SLOT_COUNT }, (_, macroId) => {
    const actions = parseSdcxMacroSlotActionsFromTab(tab, macroId);
    return {
      macroId,
      valid: actions.length > 0,
      actNum: actions.length,
      actions,
    };
  });
}
